import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, Upload, Sparkles, Download, ArrowLeft, Check as CheckIcon, Pencil, RotateCcw, Trash2, X } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { LoadingBlock } from "../components/Spinner";
import { SheetCard, StatusTag, RulerProgress, IndexTabs, type StatusTone } from "../components/Drawing";
import { NextSteps } from "../components/NextSteps";
import {
  EmptyState, Field, ModalHeader, ModalOverlay, StatusPill, dangerBtn, fmtDateOnly, input, secondaryBtn, tableHeadRow, textarea,
} from "./resilience/shared";

// Customer security questionnaires (backend/src/routes/customerQuestionnaires.routes.ts,
// lib/customerQuestionnaires.ts, docs/CUSTOMER-QUESTIONNAIRES.md). A customer's SIG, CAIQ or own
// spreadsheet or Word document is uploaded, its questions read from the file, answers drafted from the
// organisation's records with each record cited, and every answer approved by a person before the
// customer's own file is downloaded with the answers written in. "Yes" is never written without a
// record behind it. Reading is open; writing needs documents:write; drafting needs the AI tier.

type Answer = "yes" | "partial" | "no" | "not_applicable" | "not_evidenced";
type CitationKind = "soa" | "policy" | "evidence" | "record_check";
interface Citation { kind: CitationKind; id: string; label: string }
interface Progress { total: number; pending: number; drafted: number; approved: number; notEvidenced: number; ready: boolean }
interface QuestionnaireRow {
  id: string; ref: string; title: string; customerName: string | null; dueDate: string | null; fileName: string; format: string;
  extraction: string; status: string; draftError: string | null; createdAt: string; exportedAt: string | null; progress: Progress;
}
interface Item {
  id: string; position: number; ref: string | null; section: string | null; question: string; answer: Answer | null; answerText: string | null;
  confidence: "high" | "medium" | "low" | null; citations: Citation[]; origin: "ai" | "library" | "person" | null; checkNote: string | null;
  status: "pending" | "drafted" | "approved"; approvedAt: string | null; approvedBy: string | null;
}
interface Detail extends QuestionnaireRow { busy: boolean; items: Item[] }
interface Sources {
  soa: Array<{ kind: "soa"; id: string; label: string; status: string }>;
  policies: Array<{ kind: "policy"; id: string; label: string }>;
  evidence: Array<{ kind: "evidence"; id: string; label: string; current: boolean }>;
  recordChecks: Array<{ kind: "record_check"; id: string; label: string; status: string }>;
}
interface LibraryEntry { id: string; question: string; answer: Answer; answerText: string; approvedAt: string; timesReused: number }

const ANSWER_LABEL: Record<Answer, string> = { yes: "Yes", partial: "Partially", no: "No", not_applicable: "Not applicable", not_evidenced: "Not evidenced" };
const ANSWER_TONE: Record<Answer, StatusTone> = { yes: "met", partial: "partial", no: "gap", not_applicable: "none", not_evidenced: "none" };
const KIND_LABEL: Record<CitationKind, string> = { soa: "SOA", policy: "Policy", evidence: "Evidence", record_check: "Record check" };
const ORIGIN_LABEL: Record<string, string> = { ai: "Drafted by the AI", library: "From the answer library", person: "Written by a person" };
const STATUS_LABEL: Record<string, { label: string; pill: string }> = {
  reading: { label: "Reading the questions", pill: "in_progress" },
  unreadable: { label: "Questions not found", pill: "blocked" },
  open: { label: "Answering", pill: "open" },
  drafting: { label: "Drafting answers", pill: "in_progress" },
  complete: { label: "Exported", pill: "completed" },
};
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText };
const MAX_BYTES = 5 * 1024 * 1024;

export function CustomerQuestionnairesPage() {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("documents:write");
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("questionnaire");
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("questionnaire", id); else next.delete("questionnaire");
    setSearchParams(next, { replace: true });
  };
  const [list, setList] = useState<{ questionnaires: QuestionnaireRow[]; libraryCount: number } | null>(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  const load = useCallback(async () => {
    try { setList(await api.get("/customer-questionnaires")); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the questionnaires.")); }
  }, []);
  useEffect(() => { if (!openId) void load(); }, [load, openId]);

  if (openId) return <QuestionnaireView id={openId} canEdit={canEdit} onBack={() => setOpen(null)} />;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><ClipboardList size={20} /> Customer questionnaires</h1>
          <p style={{ ...pageSubtitle, maxWidth: 780 }}>
            Security questionnaires your customers send you: SIG, CAIQ, or their own spreadsheet or Word document. Upload the file as it came;
            the questions are read from it and answers drafted from your own records, each citing the records it rests on. Where nothing in the
            platform shows it, the answer is "not evidenced", never "yes". A person approves every answer before you download the customer's
            file with the answers written in.
          </p>
        </div>
        {canEdit && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setUploading(true)}>
            <Upload size={14} /> Upload a questionnaire
          </button>
        )}
      </div>
      {error && <div style={{ ...errorText, marginBottom: 12 }}>{error}</div>}
      {!list ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <SheetCard label="Questionnaires" padding={0}>
            {list.questionnaires.length === 0 ? (
              <div style={{ padding: 18 }}>
                <EmptyState title="No questionnaires yet" detail="Upload the next security questionnaire a customer sends you (.xlsx, .csv or .docx, up to 5 MB). Its questions are read from the file and nothing is answered until you ask." />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={tableHeadRow}>
                    <th style={th}>Ref</th><th style={th}>Questionnaire</th><th style={th}>Questions</th><th style={th}>Approved</th><th style={th}>Not evidenced</th><th style={th}>Status</th><th style={th}>Due</th>
                  </tr></thead>
                  <tbody>
                    {list.questionnaires.map((q) => (
                      <tr key={q.id} onClick={() => setOpen(q.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                        <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{q.ref}</td>
                        <td style={{ ...td, color: theme.color.text }}>{q.title}<div style={text.hint}>{q.customerName ? `${q.customerName} - ` : ""}{q.fileName}</div></td>
                        <td style={td}>{q.progress.total}</td>
                        <td style={td}>{q.progress.approved} of {q.progress.total}</td>
                        <td style={td}>{q.progress.notEvidenced}</td>
                        <td style={td}><StatusPill value={STATUS_LABEL[q.status]?.pill ?? "open"} label={STATUS_LABEL[q.status]?.label ?? q.status} /></td>
                        <td style={td}>{q.dueDate ? fmtDateOnly(q.dueDate) : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SheetCard>
          <SheetCard label="Answer library" actions={list.libraryCount > 0 ? <button type="button" style={secondaryBtn} onClick={() => setShowLibrary((v) => !v)}>{showLibrary ? "Hide" : "Show"}</button> : undefined}>
            <div style={text.body}>
              {list.libraryCount === 0
                ? "Every answer you approve is kept here. When the same question comes again, its approved answer is offered first, after checking the records it cites still say the same."
                : `${list.libraryCount} approved answer${list.libraryCount === 1 ? "" : "s"} kept. Each is offered again for the same question only while the records it cites still hold.`}
            </div>
            {showLibrary && <AnswerLibrary canEdit={canEdit} onChanged={load} />}
          </SheetCard>
        </div>
      )}
      {uploading && <UploadDialog onClose={() => setUploading(false)} onUploaded={(id) => { setUploading(false); setOpen(id); }} />}
    </div>
  );
}

function AnswerLibrary({ canEdit, onChanged }: { canEdit: boolean; onChanged: () => void }) {
  const [entries, setEntries] = useState<LibraryEntry[] | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setEntries((await api.get<{ entries: LibraryEntry[] }>("/customer-questionnaires/library")).entries); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the library.")); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (error) return <div style={{ ...errorText, marginTop: 10 }}>{error}</div>;
  if (!entries) return <LoadingBlock />;
  return (
    <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
      {entries.map((e) => (
        <div key={e.id} style={{ borderTop: "1px solid var(--c-border)", paddingTop: 8, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={text.itemTitle}>{e.question}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
              <StatusTag tone={ANSWER_TONE[e.answer]}>{ANSWER_LABEL[e.answer]}</StatusTag>
              <span style={text.hint}>Approved {fmtDateOnly(e.approvedAt)}; offered again {e.timesReused} time{e.timesReused === 1 ? "" : "s"}</span>
            </div>
            <div style={{ ...text.body, marginTop: 4 }}>{e.answerText}</div>
          </div>
          {canEdit && (
            <button type="button" style={secondaryBtn} aria-label="Remove from the library" onClick={async () => {
              if (!window.confirm("Stop offering this answer for the question? Answers already given are not changed.")) return;
              try { await api.del(`/customer-questionnaires/library/${e.id}`); await load(); onChanged(); } catch (err) { setError(apiErrorMessage(err, "Could not remove it.")); }
            }}><Trash2 size={13} /></button>
          )}
        </div>
      ))}
    </div>
  );
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => reject(new Error("The file could not be read"));
    r.readAsDataURL(file);
  });
}

function UploadDialog({ onClose, onUploaded }: { onClose: () => void; onUploaded: (id: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [customerName, setCustomer] = useState("");
  const [dueDate, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tooBig = !!file && file.size > MAX_BYTES;
  const steps = [
    { done: !!file && !tooBig, label: tooBig ? "A file of 5 MB or less" : "The customer's file (.xlsx, .csv or .docx)", urgent: tooBig },
    { done: title.trim().length >= 3, label: "A title you will recognise it by" },
  ];
  const submit = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const r = await api.post<{ id: string }>("/customer-questionnaires/upload", {
        fileName: file.name, data: await readBase64(file), title: title.trim(),
        customerName: customerName.trim() || null, dueDate: dueDate || null, // date-only, sent as the yyyy-mm-dd the input holds (UTC midnight on the server)
      });
      onUploaded(r.id);
    } catch (e) { setError(apiErrorMessage(e, "The questionnaire could not be uploaded.")); setBusy(false); }
  };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Upload a questionnaire" onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 14 }}>
        <NextSteps items={steps} />
        <Field label="File" help="As the customer sent it. The answers are written back into this file, in its own columns.">
          <input type="file" accept=".xlsx,.csv,.docx" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[a-z0-9]+$/i, "")); }} />
        </Field>
        <Field label="Title"><input style={input} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Customer"><input style={input} value={customerName} maxLength={200} onChange={(e) => setCustomer(e.target.value)} placeholder="Who sent it" /></Field>
        <Field label="Due back"><input type="date" style={input} value={dueDate} onChange={(e) => setDue(e.target.value)} /></Field>
        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={btnPrimary} disabled={busy || !steps.every((s) => s.done)} onClick={submit}>{busy ? "Reading the file..." : "Upload and read the questions"}</button>
        </div>
      </div>
    </ModalOverlay>
  );
}

type Filter = "all" | "review" | "pending" | "approved" | "not_evidenced";

function QuestionnaireView({ id, canEdit, onBack }: { id: string; canEdit: boolean; onBack: () => void }) {
  const [q, setQ] = useState<Detail | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [sources, setSources] = useState<Sources | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try { const d = await api.get<Detail>(`/customer-questionnaires/${id}`); setQ(d); setError(""); return d; }
    catch (e) { setError(apiErrorMessage(e, "Could not load the questionnaire.")); return null; }
  }, [id]);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const d = await load();
      if (!stop && d?.busy) timer.current = window.setTimeout(tick, 3000);
    };
    void tick();
    return () => { stop = true; if (timer.current) window.clearTimeout(timer.current); };
  }, [load]);
  const poll = () => { const t = async () => { const d = await load(); if (d?.busy) timer.current = window.setTimeout(t, 3000); }; timer.current = window.setTimeout(t, 1500); };

  const needSources = useCallback(async () => {
    if (sources || !canEdit) return;
    try { setSources(await api.get<Sources>("/customer-questionnaires/sources")); } catch { /* the editor says it has nothing to cite */ }
  }, [sources, canEdit]);

  const items = useMemo(() => {
    const all = q?.items ?? [];
    switch (filter) {
      case "review": return all.filter((i) => i.status === "drafted");
      case "pending": return all.filter((i) => i.status === "pending");
      case "approved": return all.filter((i) => i.status === "approved");
      case "not_evidenced": return all.filter((i) => i.answer === "not_evidenced");
      default: return all;
    }
  }, [q, filter]);

  if (!q) return <div className="page-pad">{error ? <div style={errorText}>{error}</div> : <LoadingBlock />}</div>;
  const locked = q.status === "complete";
  const p = q.progress;

  const draft = async () => {
    setWorking(true); setNotice("");
    try { await api.post(`/customer-questionnaires/${q.id}/draft`, {}); await load(); poll(); }
    catch (e) { setNotice(apiErrorMessage(e, "The answers could not be started.")); }
    finally { setWorking(false); }
  };
  const download = async () => {
    setWorking(true); setNotice("");
    try {
      const blob = await apiBlob(`/customer-questionnaires/${q.id}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.fileName.replace(/\.[a-z0-9]+$/i, "")} - answered.${q.format}`;
      a.click(); URL.revokeObjectURL(url);
      await load();
    } catch { setNotice("The answered file could not be downloaded. Check every answer is approved and try again."); }
    finally { setWorking(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this questionnaire and its answers? Approved answers stay in the answer library.")) return;
    try { await api.del(`/customer-questionnaires/${q.id}`); onBack(); } catch (e) { setNotice(apiErrorMessage(e, "Could not delete it.")); }
  };

  return (
    <div className="page-pad">
      <button type="button" onClick={onBack} style={{ ...secondaryBtn, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14 }}><ArrowLeft size={14} /> All questionnaires</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><ClipboardList size={20} /> {q.ref} {q.title}</h1>
          <p style={{ ...pageSubtitle, maxWidth: 780 }}>
            {q.customerName ? `From ${q.customerName}. ` : ""}{q.fileName}{q.dueDate ? `, due back ${fmtDateOnly(q.dueDate)}` : ""}.
            {q.extraction === "ai" ? " The questions were picked out of the document's text by the AI; the wording is the document's own." : " The questions were read from the file's own question column."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canEdit && !locked && p.pending > 0 && q.status === "open" && (
            <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} disabled={working} onClick={draft}>
              <Sparkles size={14} /> Draft {p.pending} answer{p.pending === 1 ? "" : "s"} from the records
            </button>
          )}
          {canEdit && (
            <button type="button" style={{ ...(p.ready ? btnPrimary : secondaryBtn), display: "flex", alignItems: "center", gap: 6 }} disabled={working || !p.ready || q.busy} onClick={download}
              title={p.ready ? undefined : "Every answer is approved before the file goes back to the customer"}>
              <Download size={14} /> Download the answered file
            </button>
          )}
          {canEdit && !locked && <button type="button" style={dangerBtn} onClick={remove}>Delete</button>}
        </div>
      </div>

      {(q.status === "reading" || q.status === "drafting") && (
        <div style={{ ...card, padding: 14, marginBottom: 14, ...text.body }}>{q.status === "reading" ? "The AI is finding the questions in the document..." : "Answers are being drafted from your records. This page updates by itself."}</div>
      )}
      {q.draftError && <div style={{ ...card, padding: 14, marginBottom: 14, ...text.body, color: theme.color.warningText }}>{q.draftError}</div>}
      {notice && <div style={{ ...errorText, marginBottom: 12 }}>{notice}</div>}
      {locked && <div style={{ ...card, padding: 14, marginBottom: 14, ...text.body }}>Exported {q.exportedAt ? fmtDateOnly(q.exportedAt) : ""}. These answers are the record of what was sent to the customer and no longer change; download the file again at any time.</div>}

      <SheetCard label="Progress" style={{ marginBottom: 16 }}>
        <RulerProgress value={p.approved} max={Math.max(p.total, 1)} label={`${p.approved} of ${p.total} answers approved`} />
        <div style={{ ...text.hint, marginTop: 8 }}>
          {p.pending} not drafted, {p.drafted} to review, {p.approved} approved; {p.notEvidenced} not evidenced by any record.
        </div>
      </SheetCard>

      <IndexTabs<Filter> value={filter} onChange={setFilter} label="Which questions to show" items={[
        { id: "all", label: "All", count: p.total },
        { id: "review", label: "To review", count: p.drafted },
        { id: "pending", label: "Not drafted", count: p.pending },
        { id: "approved", label: "Approved", count: p.approved },
        { id: "not_evidenced", label: "Not evidenced", count: p.notEvidenced },
      ]} />
      <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
        {items.length === 0 && <div style={{ ...card, padding: 16, ...text.body }}>No questions here.</div>}
        {items.map((item, i) => (
          <ItemCard key={item.id} item={item} prevSection={i > 0 ? items[i - 1].section : null} questionnaireId={q.id}
            canEdit={canEdit && !locked && !q.busy} editing={editing === item.id} sources={sources}
            onEdit={() => { void needSources(); setEditing(item.id); }} onDone={() => { setEditing(null); void load(); }} />
        ))}
      </div>
    </div>
  );
}

function ItemCard({ item, prevSection, questionnaireId, canEdit, editing, sources, onEdit, onDone }: {
  item: Item; prevSection: string | null; questionnaireId: string; canEdit: boolean; editing: boolean; sources: Sources | null; onEdit: () => void; onDone: () => void;
}) {
  const [error, setError] = useState("");
  const act = async (fn: () => Promise<unknown>) => { setError(""); try { await fn(); onDone(); } catch (e) { setError(apiErrorMessage(e, "That did not work.")); } };
  const base = `/customer-questionnaires/${questionnaireId}/items/${item.id}`;
  return (
    <div>
      {item.section && item.section !== prevSection && <div style={{ ...text.sectionTitle, margin: "10px 0 4px" }}>{item.section}</div>}
      <div style={{ ...card, padding: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ ...text.hint, minWidth: 48, fontWeight: 600 }}>{item.ref ?? `Q${item.position}`}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...text.itemTitle, whiteSpace: "pre-wrap" }}>{item.question}</div>
            {editing ? (
              <AnswerEditor item={item} sources={sources} base={base} onCancel={onDone} onSaved={onDone} />
            ) : item.answer ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <StatusTag tone={ANSWER_TONE[item.answer]}>{ANSWER_LABEL[item.answer]}</StatusTag>
                  {item.status === "approved"
                    ? <StatusPill value="approved" label={`Approved${item.approvedBy ? ` by ${item.approvedBy}` : ""}`} />
                    : <StatusPill value="pending" label="To review" />}
                  {item.confidence && <span style={text.hint}>Confidence {item.confidence}</span>}
                  {item.origin && <span style={text.hint}>{ORIGIN_LABEL[item.origin]}</span>}
                </div>
                {item.answerText && <div style={{ ...text.body, marginTop: 6, whiteSpace: "pre-wrap" }}>{item.answerText}</div>}
                <Citations citations={item.citations} />
                {item.checkNote && <div style={{ ...text.hint, marginTop: 6, color: theme.color.warningText }}>{item.checkNote}</div>}
              </div>
            ) : (
              <div style={{ ...text.hint, marginTop: 6 }}>Not drafted yet.</div>
            )}
            {error && <div style={{ ...errorText, marginTop: 6 }}>{error}</div>}
          </div>
          {canEdit && !editing && (
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {item.status === "drafted" && item.answer && (
                <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 4 }}
                  onClick={() => act(() => api.put(base, { answer: item.answer, answerText: item.answerText ?? "", citations: item.citations.map((c) => ({ kind: c.kind, id: c.id })), approve: true }))}>
                  <CheckIcon size={13} /> Approve
                </button>
              )}
              <button type="button" style={secondaryBtn} aria-label="Edit the answer" title="Edit" onClick={onEdit}><Pencil size={13} /></button>
              {item.status === "approved" && (
                <button type="button" style={secondaryBtn} aria-label="Take back the approval" title="Take back the approval" onClick={() => act(() => api.post(`${base}/reopen`, {}))}><RotateCcw size={13} /></button>
              )}
              {item.status === "drafted" && (
                <button type="button" style={secondaryBtn} aria-label="Clear the draft so it is drafted again" title="Clear the draft" onClick={() => act(() => api.post(`${base}/reopen`, { clear: true }))}><X size={13} /></button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Citations({ citations }: { citations: Citation[] }) {
  if (citations.length === 0) return <div style={{ ...text.hint, marginTop: 6 }}>No record cited.</div>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {citations.map((c) => (
        <span key={`${c.kind}:${c.id}`} style={{ ...text.hint, border: "1px solid var(--c-border)", borderRadius: 2, padding: "1px 7px", background: "var(--c-panelAlt)" }}>
          <strong>{KIND_LABEL[c.kind]}</strong> {c.label}
        </span>
      ))}
    </div>
  );
}

function AnswerEditor({ item, sources, base, onCancel, onSaved }: { item: Item; sources: Sources | null; base: string; onCancel: () => void; onSaved: () => void }) {
  const [answer, setAnswer] = useState<Answer>(item.answer ?? "not_evidenced");
  const [answerText, setText] = useState(item.answerText ?? "");
  const [citations, setCitations] = useState<Citation[]>(item.citations);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const options = useMemo(() => {
    if (!sources) return [];
    const supporting = (s: { kind: CitationKind; status?: string; current?: boolean }) =>
      s.kind === "policy" || (s.kind === "soa" && s.status === "COMPLIANT") || (s.kind === "evidence" && s.current) || (s.kind === "record_check" && s.status === "pass");
    return [
      ...sources.policies.map((s) => ({ ...s, note: "" })), ...sources.soa.map((s) => ({ ...s, note: s.status.replace(/_/g, " ").toLowerCase() })),
      ...sources.evidence.map((s) => ({ ...s, note: s.current ? "" : "out of date" })), ...sources.recordChecks.map((s) => ({ ...s, note: s.status })),
    ].map((s) => ({ key: `${s.kind}:${s.id}`, kind: s.kind as CitationKind, id: s.id, label: s.label, note: s.note, supports: supporting(s) }));
  }, [sources]);
  const supported = citations.some((c) => options.find((o) => o.key === `${c.kind}:${c.id}`)?.supports);
  const steps = [
    { done: answer !== "yes" || supported, label: "A yes cites a record that shows it: an adopted policy, a current document, a compliant SOA row or a passed record check", urgent: answer === "yes" && !supported },
    { done: answer === "not_evidenced" || answerText.trim().length > 0, label: "The words the customer will read" },
  ];
  const save = async (approve: boolean) => {
    setBusy(true); setError("");
    try { await api.put(base, { answer, answerText, citations: citations.map((c) => ({ kind: c.kind, id: c.id })), approve }); onSaved(); }
    catch (e) { setError(apiErrorMessage(e, "The answer could not be saved.")); setBusy(false); }
  };
  return (
    <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
      <NextSteps items={steps} />
      <Field label="Answer">
        <select style={{ ...input, width: "auto" }} value={answer} onChange={(e) => setAnswer(e.target.value as Answer)}>
          {(Object.keys(ANSWER_LABEL) as Answer[]).map((a) => <option key={a} value={a}>{ANSWER_LABEL[a]}</option>)}
        </select>
      </Field>
      <Field label="What the customer reads"><textarea style={textarea} rows={4} maxLength={4000} value={answerText} onChange={(e) => setText(e.target.value)} /></Field>
      <Field label="Records it rests on" help="Only your organisation's own records can be cited.">
        <Citations citations={citations} />
        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          {citations.map((c) => (
            <button key={`${c.kind}:${c.id}`} type="button" style={secondaryBtn} onClick={() => setCitations(citations.filter((x) => !(x.kind === c.kind && x.id === c.id)))}>Remove {KIND_LABEL[c.kind]}: {c.label.slice(0, 40)}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <select style={input} value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Record to cite">
            <option value="">{sources ? "Choose a record to cite" : "Loading your records..."}</option>
            {(["policy", "soa", "evidence", "record_check"] as CitationKind[]).map((k) => (
              <optgroup key={k} label={KIND_LABEL[k]}>
                {options.filter((o) => o.kind === k && !citations.some((c) => c.kind === o.kind && c.id === o.id)).map((o) => (
                  <option key={o.key} value={o.key}>{o.label}{o.note ? ` (${o.note})` : ""}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <button type="button" style={secondaryBtn} disabled={!pick} onClick={() => {
            const o = options.find((x) => x.key === pick);
            if (o) setCitations([...citations, { kind: o.kind, id: o.id, label: o.label }]);
            setPick("");
          }}>Cite</button>
        </div>
      </Field>
      {error && <div style={errorText}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" style={secondaryBtn} onClick={onCancel}>Cancel</button>
        <button type="button" style={secondaryBtn} disabled={busy} onClick={() => save(false)}>Save</button>
        <button type="button" style={btnPrimary} disabled={busy || !steps.every((s) => s.done)} onClick={() => save(true)}>Save and approve</button>
      </div>
    </div>
  );
}
